import UserAuthStore from '../store/userStore';
import logger from './Logger';

export const AUTH_SESSION_KEYS = [
  'session_token',
  'user_id',
  'org_id',
  'auth_version',
];

export const AUTH_SESSION_EVENT_KEY = 'photon_auth_session_event';
export const AUTH_SESSION_CHANGE_EVENT = 'photon-auth-session-change';
const invalidationHandlers = new Set();

export const registerAuthSessionInvalidationHandler = (handler) => {
  invalidationHandlers.add(handler);
  return () => invalidationHandlers.delete(handler);
};

const publishSessionChange = (detail, notifyCurrentTab = false) => {
  const event = {
    ...detail,
    id: `${Date.now()}-${Math.random()}`,
    timestamp: new Date().toISOString(),
  };
  localStorage.setItem(AUTH_SESSION_EVENT_KEY, JSON.stringify(event));
  if (notifyCurrentTab) {
    window.dispatchEvent(new CustomEvent(AUTH_SESSION_CHANGE_EVENT, { detail: event }));
  }
};

export const getSessionToken = () => localStorage.getItem('session_token');

export const persistAuthSession = ({ sessionToken, userId, orgId }, source = 'login') => {
  localStorage.setItem('session_token', sessionToken);
  localStorage.setItem('user_id', userId);
  localStorage.setItem('org_id', orgId);
  localStorage.setItem('auth_version', '2');
  publishSessionChange({ type: 'restored', source });
};

export const markAuthSessionCurrent = (expectedToken) => {
  if (!expectedToken || getSessionToken() !== expectedToken) {
    return false;
  }
  localStorage.setItem('auth_version', '2');
  return true;
};

export const invalidateAuthSession = ({
  reason,
  source,
  expectedToken = getSessionToken(),
  notifyCurrentTab = true,
} = {}) => {
  if (getSessionToken() !== expectedToken) {
    return false;
  }

  const authState = UserAuthStore.getState();
  const hadSessionState = AUTH_SESSION_KEYS.some((key) => localStorage.getItem(key) !== null)
    || authState.user !== null
    || authState.orgId !== ''
    || authState.authStatus !== 'unauthenticated';
  if (!hadSessionState) {
    return false;
  }

  AUTH_SESSION_KEYS.forEach((key) => localStorage.removeItem(key));
  UserAuthStore.getState().clearAuthSession();
  logger.warn('AuthSession', 'Photon session invalidated', { reason, source });
  publishSessionChange({ type: 'invalidated', reason, source }, notifyCurrentTab);
  invalidationHandlers.forEach((handler) => handler({ reason, source }));
  return true;
};

export const subscribeToAuthSessionChanges = (callback) => {
  const onLocalChange = (event) => callback(event.detail);
  const onStorageChange = (event) => {
    if (event.key !== AUTH_SESSION_EVENT_KEY || !event.newValue) return;
    try {
      callback(JSON.parse(event.newValue));
    } catch (error) {
      logger.warn('AuthSession', 'Ignoring malformed cross-tab auth event', {
        error: error.message,
      });
    }
  };

  window.addEventListener(AUTH_SESSION_CHANGE_EVENT, onLocalChange);
  window.addEventListener('storage', onStorageChange);
  return () => {
    window.removeEventListener(AUTH_SESSION_CHANGE_EVENT, onLocalChange);
    window.removeEventListener('storage', onStorageChange);
  };
};